import axios, { type InternalAxiosRequestConfig } from "axios";

const HTTP_TIMEOUT_MS = 15_000;
const RETRY_DELAY_MS = 350;
const MAX_RETRIES = 1;

type RetriableConfig = InternalAxiosRequestConfig & {
	_retryCount?: number;
};

export const apiClient = axios.create({
	withCredentials: true,
	timeout: HTTP_TIMEOUT_MS,
});

export const isApiError = axios.isAxiosError;

function retryDelay(): Promise<void> {
	return new Promise((resolve) => {
		window.setTimeout(resolve, RETRY_DELAY_MS);
	});
}

function isRetriableMethod(method: string | undefined): boolean {
	const normalized = method?.toUpperCase() ?? "GET";
	return normalized === "GET" || normalized === "HEAD" || normalized === "OPTIONS";
}

function isRetriableStatus(status: number | undefined): boolean {
	return status !== undefined && status >= 500 && status < 600;
}

apiClient.interceptors.response.use(undefined, async (error: unknown) => {
	if (!isApiError(error) || !error.config) {
		return Promise.reject(error);
	}
	const config = error.config as RetriableConfig;
	config._retryCount = config._retryCount ?? 0;
	const status = error.response?.status;
	const transientFailure =
		error.code === "ECONNABORTED" ||
		error.code === "ERR_NETWORK" ||
		isRetriableStatus(status);
	if (
		config._retryCount >= MAX_RETRIES ||
		!transientFailure ||
		!isRetriableMethod(config.method)
	) {
		return Promise.reject(error);
	}
	config._retryCount += 1;
	await retryDelay();
	return apiClient.request(config);
});
